import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

import { useClerk, useReverification } from "@clerk/nextjs"
import {
  isClerkAPIResponseError,
  isReverificationCancelledError,
} from "@clerk/nextjs/errors"
import type { SessionVerificationLevel } from "@clerk/nextjs/types"
import { toast } from "@coval/ui/toast"

import { ClerkReverificationDialog } from "./ClerkReverificationDialog"

interface VerificationRequest {
  level: SessionVerificationLevel | undefined
  complete: () => void
  cancel: () => void
}
type ActionResult<T> = { value: T } | undefined
interface ClerkActions {
  busy: boolean
  reverify: <T>(task: () => Promise<T>) => Promise<T>
  run: <T>(
    message: string | null,
    task: (assertCurrent: () => void) => Promise<T>,
    organizationId?: string,
    allowIdentityChange?: boolean,
    options?: { throwOnError?: boolean },
  ) => Promise<ActionResult<T>>
}
const Context = createContext<ClerkActions | null>(null)

export function ClerkActionProvider({ children }: { children: ReactNode }) {
  const clerk = useClerk()
  const [busy, setBusy] = useState(false)
  const [verification, setVerification] = useState<VerificationRequest | null>(
    null,
  )
  const pending = useRef<VerificationRequest | null>(null)
  const running = useRef(false)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
      pending.current?.cancel()
      pending.current = null
    }
  }, [])
  const execute = useReverification(
    async (task: () => Promise<unknown>) => task(),
    {
      onNeedsReverification: (request) => {
        if (!mounted.current) {
          request.cancel()
          return
        }
        pending.current?.cancel()
        pending.current = request
        setVerification(request)
      },
    },
  )

  const run = useCallback(
    async <T,>(
      message: string | null,
      task: (assertCurrent: () => void) => Promise<T>,
      organizationId?: string,
      allowIdentityChange = false,
      options?: { throwOnError?: boolean },
    ): Promise<ActionResult<T>> => {
      if (running.current) {
        return undefined
      }
      if (!clerk.user || !clerk.session) {
        if (options?.throwOnError) {
          throw new Error("Your account changed. Reopen Settings to continue.")
        }
        return undefined
      }
      const userId = clerk.user.id
      const sessionId = clerk.session.id
      const current = () =>
        mounted.current &&
        clerk.user?.id === userId &&
        clerk.session?.id === sessionId &&
        (!organizationId || clerk.organization?.id === organizationId)
      running.current = true
      setBusy(true)
      try {
        // Reverification retries the callback, so check its captured identity before every attempt.
        const assertCurrent = () => {
          if (!current()) {
            throw new Error(
              "Your account or organization changed. Reopen Settings to continue.",
            )
          }
        }
        const value = await execute(async () => {
          assertCurrent()
          return task(assertCurrent)
        })
        if (!current() && !allowIdentityChange) {
          return undefined
        }
        if (message && current()) {
          toast.success({ title: message })
        }
        return { value: value as T }
      } catch (error) {
        if (current() && !isReverificationCancelledError(error)) {
          toast.error({
            error,
            title: isClerkAPIResponseError(error)
              ? error.errors[0]?.longMessage ||
                error.errors[0]?.message ||
                error.message
              : error instanceof Error
                ? error.message
                : "Could not save your changes. Please try again.",
          })
        }
        if (options?.throwOnError && !isReverificationCancelledError(error)) {
          throw error
        }
        return undefined
      } finally {
        running.current = false
        if (mounted.current) {
          setBusy(false)
        }
      }
    },
    [clerk, execute],
  )
  const reverify = useCallback(
    async <T,>(task: () => Promise<T>): Promise<T> => {
      const userId = clerk.user?.id
      const sessionId = clerk.session?.id
      return (await execute(async () => {
        if (
          !mounted.current ||
          !userId ||
          clerk.user?.id !== userId ||
          clerk.session?.id !== sessionId
        ) {
          throw new Error(
            "Your account changed. Reopen Settings before continuing.",
          )
        }
        return task()
      })) as T
    },
    [clerk, execute],
  )
  const actions = useMemo(
    () => ({ busy, run, reverify }),
    [busy, run, reverify],
  )
  const closeVerification = (complete: boolean) => {
    const request = pending.current
    pending.current = null
    setVerification(null)
    if (complete) {
      request?.complete()
    } else {
      request?.cancel()
    }
  }
  return (
    <Context.Provider value={actions}>
      {children}
      {verification && (
        <ClerkReverificationDialog
          level={verification.level ?? "first_factor"}
          onComplete={() => closeVerification(true)}
          onCancel={() => closeVerification(false)}
        />
      )}
    </Context.Provider>
  )
}

export function useClerkActions() {
  const value = useContext(Context)
  if (!value) {
    throw new Error("ClerkActionProvider is required")
  }
  return value
}
